"use client";

import { useCallback } from "react";
import { toast } from "react-toastify";
import PrizeTicketSelector from "@/app/components/PrizeTicketSelector/prizeTicketSelector";
import ToastNotification from "@/app/components/ToastNotification/toastNotification";
import { publishModalData, publishModalVisibility } from "@/app/redux/features/modal/modalSlice";
import { useAddTicketsForAllCompetitionsMutation, useAddTicketsMutation } from "@/app/redux/features/api/lifestyle";
import type { LifestylePrize } from "@/app/redux/features/api/lifestyle.types";
import { useAppDispatch } from "@/app/redux/hooks";
import type { AddTicketsData } from "@/app/common";
import { sendCustomEvent } from "@/gtm/functions";
import { ADD_TO_CART_METHOD } from "../lifestyle-constants";

/** AddTicketStatusResult values returned by CartController. */
const STATUS = {
  success: 1,
  limitExceeded: 2,
  loyaltyTooLow: 3,
  generalError: 4,
  partiallyAdded: 5,
} as const;

interface UseAddToBasketArgs {
  /** From useDailyCompetitions; a prize can run across several daily competitions. */
  competitionIdsForPrize: (prize: LifestylePrize) => number[];
}

/**
 * Adding tickets, both the quick-add on a card and the full ticket selector.
 *
 * The modal path delegates to PrizeTicketSelector, which already dispatches the
 * `addToBasketApiEvent` bridge that the Angular cart and shoppingCart.tsx listen
 * for. The quick-add path has to dispatch that event itself.
 */
export default function useAddToBasket({ competitionIdsForPrize }: UseAddToBasketArgs) {
  const dispatch = useAppDispatch();
  const [addTickets, addTicketsState] = useAddTicketsMutation();
  const [addTicketsForAllCompetitions, addAllState] = useAddTicketsForAllCompetitionsMutation();

  /**
   * Publishes the result to the cart. The two Redux stores (this embed and the
   * header embed) are separate in the MVC environment, so the handoff is a
   * CustomEvent rather than a dispatch.
   */
  const publishToCart = useCallback((data: AddTicketsData, prizeId: number) => {
    dispatchEvent(
      new CustomEvent("addToBasketApiEvent", {
        detail: { addToBasketApiData: JSON.stringify(data), prizeId },
      })
    );
  }, []);

  const notify = useCallback((data: AddTicketsData) => {
    switch (data.AddTicketStatusResult) {
      case STATUS.success:
        return true;
      case STATUS.partiallyAdded:
        toast.info(
          <ToastNotification
            message={`Adding ${data.TicketsRequested} ticket${data.TicketsRequested > 1 ? "s" : ""} to your basket would exceed the ticket limit (${data.TicketLimit}) for this Competition. ${data.TicketsAdded} ticket${data.TicketsAdded > 1 ? "s have" : " has"} therefore been added.`}
          />
        );
        return true;
      case STATUS.limitExceeded:
        toast.error(
          <ToastNotification
            message={`You have reached the maximum permitted quantity of ${data.TicketLimit} tickets for this competition.`}
          />
        );
        return false;
      case STATUS.loyaltyTooLow:
        toast.error(<ToastNotification message="Your loyalty level is too low to add tickets for this prize." />);
        return false;
      case STATUS.generalError:
      default:
        toast.error(
          <ToastNotification message="We have been unable to add tickets to your basket. Please reload the page and try again." />
        );
        return false;
    }
  }, []);

  /** Adds tickets straight from a card, without opening the selector. */
  const quickAdd = useCallback(
    async (prize: LifestylePrize, quantity = 1) => {
      const competitionIds = competitionIdsForPrize(prize);

      try {
        // A daily prize that spans several days is entered into all of them at
        // once; a single-competition prize takes the simpler endpoint.
        const data =
          competitionIds.length > 1
            ? await addTicketsForAllCompetitions({
                competitionIdListString: competitionIds.join(","),
                prizeContentNodeId: prize.ContentNodeId,
                quantityToAdd: quantity,
                addToCartMethodId: ADD_TO_CART_METHOD.quickAdd,
              }).unwrap()
            : await addTickets({
                competitionId: competitionIds[0] ?? prize.CompetitionId,
                prizeContentNodeId: prize.ContentNodeId,
                quantityToAdd: quantity,
                addToCartMethodId: ADD_TO_CART_METHOD.quickAdd,
              }).unwrap();

        if (data.TrackingData?.tiktok) {
          sendCustomEvent(data.TrackingData.tiktok);
        }

        if (notify(data)) {
          publishToCart(data, prize.ContentNodeId);
        }

        return data;
      } catch (error) {
        console.error("Failed to add lifestyle prize tickets to the basket", error);
        toast.error(
          <ToastNotification message="We have been unable to add another ticket to your basket, please try again or <a href='/contact-us'>get in touch.</a>" />
        );
        return null;
      }
    },
    [addTickets, addTicketsForAllCompetitions, competitionIdsForPrize, notify, publishToCart]
  );

  /** Opens the full ticket selector for a prize. */
  const openTicketSelector = useCallback(
    (prize: LifestylePrize) => {
      dispatch(
        publishModalData({
          modalData: (
            <PrizeTicketSelector prizeId={prize.ContentNodeId} addToCartMethodId={ADD_TO_CART_METHOD.ticketSelector} />
          ),
        })
      );
      dispatch(publishModalVisibility({ isOpen: true }));
    },
    [dispatch]
  );

  return {
    quickAdd,
    openTicketSelector,
    isAdding: addTicketsState.isLoading || addAllState.isLoading,
  };
}
